/**
 * CreateEngagementDialog — admin picks a template, enters a **start date**,
 * and the engagement is created. event_date is derived as start_date + length_days
 * so the existing event-anchored offsets map to the start-anchored checklist
 * the user wants to see (Day 0 == start_date).
 */
import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Calendar, FileCheck, ListChecks, Users } from "lucide-react";

function addDaysISO(iso, days) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function CreateEngagementDialog({ open, onOpenChange, clientId, clientName, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [startDate, setStartDate] = useState(todayISO());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/engagement-templates").then((r) => {
      setTemplates(r.data || []);
      if (!templateId && r.data?.length) setTemplateId(r.data.find((t) => t.id === "rtso-30")?.id || r.data[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const picked = templates.find((t) => t.id === templateId);
  const eventDate = picked ? addDaysISO(startDate, picked.length_days) : "";

  const submit = async () => {
    if (!picked || !startDate) { toast.error("Pick a template and a start date"); return; }
    setBusy(true);
    try {
      await api.post("/engagements", {
        client_id: clientId,
        template_id: picked.id,
        event_date: eventDate,
        length_days: picked.length_days,
      });
      toast.success("Engagement created");
      onCreated && onCreated();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-lg" data-testid="create-engagement-dialog">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 text-[15px] font-display font-semibold">
            New engagement {clientName && <span className="text-zinc-500 text-[12px] font-normal"> · {clientName}</span>}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-[11px]">
            Pick a checklist template and the day you want the engagement to begin. Every task date auto-fills from the template; you can edit any date later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Template</Label>
            <div className="mt-1.5 grid grid-cols-1 gap-2" data-testid="create-engagement-templates">
              {templates.map((t) => {
                const on = t.id === templateId;
                return (
                  <button key={t.id} type="button" onClick={() => setTemplateId(t.id)}
                    data-testid={`create-engagement-template-${t.id}`}
                    className={`text-left p-3 rounded border transition-all ${
                      on ? "border-white bg-zinc-900/60" : "border-zinc-800 bg-[#0a0a0a] hover:border-zinc-600"
                    }`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <ListChecks className={`w-3.5 h-3.5 ${on ? "text-emerald-300" : "text-zinc-500"}`} />
                        <span className="text-zinc-100 text-[13px] font-display font-semibold">{t.name}</span>
                      </div>
                      <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
                        {t.length_days}d
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[10.5px] text-zinc-500 font-mono">
                      <span className="flex items-center gap-1"><FileCheck className="w-2.5 h-2.5" /> {(t.deliverables || []).length} deliverables</span>
                      <span className="flex items-center gap-1"><Users className="w-2.5 h-2.5" /> {(t.meetings || []).length} meetings</span>
                      <span className="flex items-center gap-1"><Calendar className="w-2.5 h-2.5" /> {(t.phases || []).length} phases</span>
                    </div>
                  </button>
                );
              })}
              {templates.length === 0 && (
                <div className="text-zinc-500 text-[12px] px-1 py-3">Loading templates…</div>
              )}
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Start date (Day 0)</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="bg-[#0a0a0a] border-zinc-800 text-[13px] h-9 mt-1"
              data-testid="create-engagement-start-date" />
            {picked && eventDate && (
              <div className="text-[11px] text-zinc-500 font-mono mt-1.5" data-testid="create-engagement-preview">
                → Event day (Day {picked.length_days}): <span className="text-zinc-200">{eventDate}</span>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}
              className="text-zinc-500 h-8 text-[12px]" data-testid="create-engagement-cancel">Cancel</Button>
            <Button size="sm" onClick={submit} disabled={busy || !picked}
              className="bg-white text-black hover:bg-zinc-200 h-8 text-[12px]"
              data-testid="create-engagement-submit">
              {busy ? "Creating…" : "Create engagement"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
